import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Druido L6-10 (manuale V1, rr. 3236-3316). 14 mosse.
 *
 * Pattern notabili:
 * - **3 mosse richiedono *Mutaforma*** (Svincolato dalla Forma, Chimera,
 *   Danza del Doppleganger).
 * - **`prerequisites.allOf`**: *Magia Fatata* richiede *Il Regno Fatato* +
 *   *Mana* (forward-reference cross-class a `chierico.mana` o `mago.mana`,
 *   semantica del Druido che ha *Mana* come parte di *Divinità della
 *   Natura*). *Il Folletto* richiede *Il Regno Fatato* + *Mutaforma*.
 * - **Cross-class restriction `prerequisites.anyOf`**: *Spiriti Guardiani*
 *   richiede *Lingua degli Spiriti* OR *Portavoce dell'Aldilà* (Sciamano).
 * - **Mossa multiclasse**: *Sorella del Predatore* (analogo a *Fratello
 *   del Cacciatore* in L2-5).
 */

const svincolatoDallaForma: Move = {
  id: 'druido.svincolato-dalla-forma',
  name: 'Svincolato dalla Forma',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando usi *Mutaforma*',
  description: 'Tira 1d4 e aggiungi il risultato al numero di prese ottenute.',
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const chimera: Move = {
  id: 'druido.chimera',
  name: 'Chimera',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando usi *Mutaforma*',
  description:
    'Puoi creare una forma composta da due o tre esseri diversi, per esempio un orso con le ali di aquila e la testa di cervo. La forma di *Chimera* segue le stesse regole di *Mutaforma*, ma il GM ti dice almeno una mossa legata alla natura di ciascun essere.',
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const danzaDelDoppleganger: Move = {
  id: 'druido.danza-del-doppleganger',
  name: 'Danza del Doppleganger',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: "Quando usi *Studiare l'Essenza* su un individuo specifico, o assumi una forma esatta",
  description:
    "Puoi *Studiare l'Essenza* di individui specifici per assumere la loro forma esatta, inclusi uomini, elfi e simili. Puoi anche nascondere il tuo Segno Rivelatore, ma se lo fai prendi -1 continuato a tutti i tiri per utilizzare le mosse finché non ritorni al tuo aspetto normale.",
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const maestroDiForme: Move = {
  id: 'druido.maestro-di-forme',
  name: 'Maestro di Forme',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando usi *Mutaforma*',
  description:
    'Scegli se aumentare la tua Armatura di 1 o infliggere +1d4 danni con i tuoi attacchi che causano danno. Il beneficio dura fintanto che non torni alla tua forma normale.',
  tags: [],
  prerequisites: ['druido.artigiano-di-forme'],
  replaces: [],
};

const coluiCheParlaAlMondo: Move = {
  id: 'druido.colui-che-parla-al-mondo',
  name: 'Colui che Parla al Mondo',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando percepisci gli elementi puri (fuoco, acqua, aria e terra)',
  description:
    "Riesci a percepire gli elementi puri (fuoco, acqua, aria e terra) che costituiscono la materia di cui è fatto il mondo, e il GM ne terrà conto quando descrive ciò che ti circonda. Puoi applicare *Studiare l'Essenza*, *Mutaforma*, *Lingua degli Spiriti* e *Portavoce dell'Aldilà* (Sciamano) agli elementali.\n\nQuando **sei in una forma elementale (anche come parte di *Chimera*)**, ottieni le etichette *fatto di [elemento]*, *immune a [elemento]* e *vulnerabile a [elemento opposto]*, e la mossa «*Quando **manipoli l'elemento di cui sei fatto**, spendi una presa di *Mutaforma* e apporti una modifica temporanea alla forma o all'intensità di ciò che stai manipolando*». Inoltre, in base all'elemento prescelto, ottieni anche le seguenti peculiarità:\n\n- *fuoco*: puoi colpire bersagli a portata *corta*, *media*, *lunga* e *vicino*, e i tuoi attacchi hanno le etichette *2 perforante* e *brutale*, ma hai la mossa «*Quando **fai qualcosa che non infligge danni a cose o creature**, perdi una presa di *Mutaforma*»;\n- *acqua*: puoi respirare e muoverti allo stesso modo sia dentro l'acqua che fuori, e hai la mossa «*Quando **devi passare in uno spazio ristretto**, spendi una presa di *Mutaforma* e ci passi*»;\n- *aria*: puoi volare, e hai accesso alla mossa «*Quando **sprigioni l'elettricità statica che hai accumulato**, spendi una presa di *Mutaforma* e infliggi il tuo dado di Danno a chiunque si trovi entro distanza *media* da te*»;\n- *terra*: ottieni +2 Armatura, ignori l'effetto dell'etichetta *impatto* degli attacchi che subisci e ottieni la mossa «*Quando **ti vuoi fondere con la terra o con la pietra, oppure attraversare tali materiali**, spendi una presa di *Mutaforma* e lo fai*».\n\nIl GM può darti anche altre capacità, debolezze o mosse legate alla tua forma elementale.",
  tags: [],
  prerequisites: ['druido.colui-che-parla-alle-cose'],
  replaces: [],
};

const maestriaElementale: Move = {
  id: 'druido.maestria-elementale',
  name: 'Maestria Elementale',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: "Quando scegli le condizioni per ottenere l'effetto voluto con *Plasmare gli Elementi*",
  description: 'Ne devi scegliere una in meno del normale.',
  tags: [],
  prerequisites: ['druido.plasmare-gli-elementi'],
  replaces: [],
};

const tessitoreDiClima: Move = {
  id: 'druido.tessitore-di-clima',
  name: 'Tessitore di Clima',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: "Quando sei all'aperto mentre il sole sorge",
  description:
    'Dì al GM che tempo farà oggi, e la tua previsione si avvererà. Il meteo sarà quello che hai detto fino alla distanza di circa un giorno di viaggio da dove ti trovi.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilFiglioPrediletto: Move = {
  id: 'druido.il-figlio-prediletto',
  name: 'Il Figlio Prediletto',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger:
    'Una volta al giorno, se ti trovi immerso nella natura e subisci un danno che ti ucciderebbe',
  description:
    'Descrivi con quale prodigio incredibile e miracoloso gli spiriti ti salvano. Il danno viene negato, ma prima o poi ti verrà presentato il conto.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilSonnoDelDruido: Move = {
  id: 'druido.il-sonno-del-druido',
  name: 'Il Sonno del Druido',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger:
    'Quando trascorri del tempo in un luogo sicuro e appropriato in una Terra diversa da quelle con cui sei già affine, e gli spiriti che vi risiedono ti sono favorevoli',
  description:
    'Puoi entrare in sintonia con quella Terra. Il GM ti dirà quanto tempo ti occorre, quali rituali devi officiare e che prezzo devi pagare. Conclusa la cerimonia, sei considerato *Nato* anche dalla nuova Terra.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const spiritiGuardiani: Move = {
  id: 'druido.spiriti-guardiani',
  name: 'Spiriti Guardiani',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger:
    'Quando prometti di proteggere uno spirito ben disposto nei tuoi confronti e con cui puoi comunicare',
  description:
    'Se accetta, formi con esso un legame che gli consente di avvisarti se è in pericolo. Anche tu puoi contattarlo per avere informazioni su ciò che accade nel luogo in cui si trova. Se vieni meno a una promessa di protezione, però, non potrai più comunicare con i tuoi *Spiriti Guardiani* o usare questa mossa fintanto che non avrai posto rimedio alla tua mancanza.',
  tags: [],
  prerequisites: [
    {
      anyOf: ['druido.lingua-degli-spiriti', 'sciamano.portavoce-dell-aldila'],
    },
  ],
  replaces: [],
};

const magiaFatata: Move = {
  id: 'druido.magia-fatata',
  name: 'Magia Fatata',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli con il GM due incantesimi dalla lista del Mago tematicamente coerenti con il regno fatato, che siano di livello al massimo pari al tuo da Incantatore. Potrai sempre lanciare tali incantesimi e il trucchetto Prestidigitazione come se facessero parte del tuo repertorio.',
  tags: [],
  prerequisites: [
    {
      // Manuale: richiede *Il Regno Fatato* + *Mana*. Il Druido ottiene
      // *Mana* come side-effect di *Divinità della Natura* (manuale r. 3103),
      // quindi traduciamo "ha Mana" come "ha scelto Divinità della Natura
      // come mossa iniziale di scelta".
      allOf: ['druido.il-regno-fatato', 'druido.divinita-della-natura'],
    },
  ],
  replaces: [],
};

const ilFolletto: Move = {
  id: 'druido.il-folletto',
  name: 'Il Folletto',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando assumi una forma fatata',
  description:
    "Ora sei in grado di usare *Mutaforma* per assumere l'aspetto di qualunque creatura fatata, anche umanoide o mostruosa. Quando assumi una forma fatata, ottieni sempre due o più mosse associate a tale forma (anche quando fa parte di *Chimera*), di cui almeno una sia legata alla manifestazione della magia fatata e abbia l'effetto di un qualsiasi incantesimo di 1° livello del Mago (o qualcosa di simile, a scelta e discrezione del GM).",
  tags: [],
  prerequisites: [
    {
      allOf: ['druido.il-regno-fatato', 'druido.mutaforma'],
    },
  ],
  replaces: [],
};

const paceInteriore: Move = {
  id: 'druido.pace-interiore',
  name: 'Pace Interiore',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando guadagni prese usando la mossa *Equilibrio*',
  description: 'Con 7+, ne ottieni una in più del normale. Con 12+, ottieni 4 prese.',
  tags: [],
  prerequisites: ['druido.equilibrio'],
  replaces: [],
};

const sorellaDelPredatore: Move = {
  id: 'druido.sorella-del-predatore',
  name: 'Sorella del Predatore',
  type: 'advanced_6_10',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli una mossa non multiclasse del Chierico o del Ranger.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool Chierico/Ranger + Bardo se *La Canzone delle Fate* posseduta.
  // FI-43 (2026-05-15): + Sciamano se archetipo Nano (simmetrico a *Fratello del Cacciatore*).
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['chierico', 'ranger'] },
    extendsByArchetype: [{ archetypeId: 'nano', extraClasses: ['sciamano'] }],
    extendsByMove: [{ moveId: 'druido.la-canzone-delle-fate', extraClasses: ['bardo'] }],
  },
};

export const druidoAdvanced6to10Moves: readonly Move[] = [
  svincolatoDallaForma,
  chimera,
  danzaDelDoppleganger,
  maestroDiForme,
  coluiCheParlaAlMondo,
  maestriaElementale,
  tessitoreDiClima,
  ilFiglioPrediletto,
  ilSonnoDelDruido,
  spiritiGuardiani,
  magiaFatata,
  ilFolletto,
  paceInteriore,
  sorellaDelPredatore,
] as const;
